import { useCallback, useEffect, useState } from "react";
import type { PatientDetails } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CustomerDetailsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; details: PatientDetails };

// No cache, unlike usePatient — the Details tab is the only reader of this
// endpoint and it's fetched once per visit, so there is nothing to warm.
// `setDetails` lets the caller replace the loaded record with a PATCH
// response after a successful contact save, without a second GET.
export function useCustomerDetails(id: string | undefined): [CustomerDetailsState, (details: PatientDetails) => void] {
  const api = useApi();
  const [state, setState] = useState<CustomerDetailsState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .details(id)
      .then((details) => {
        if (!cancelled) {
          setState({ status: "success", details });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load details" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const setDetails = useCallback((details: PatientDetails) => {
    setState({ status: "success", details });
  }, []);

  return [state, setDetails];
}
